import { localizedStrings } from '@core/strings'
import React, { useState, useRef, useEffect } from 'react'
import { useModal } from '@common/GlobalModal'
import { Box, Flex, Text } from '@fundamentals'
import { alpha } from '@mui/material'
import { PermitDetailsModal } from '@modules/permitDetails/PermitDetailsModal'
import { Permit } from '@core/entities/permit/Permit'
import { PermitCard } from '@modules/permitDetails/PermitCard'
import { CanvasState } from '@core/entities/skia/CanvasState'
import { ProjectPlanPermits } from '@core/entities/project/ProjectPlanPermits'

type ProjectPlanPermitsSidebarProps = {
  planPermits: ProjectPlanPermits
  canvasState: CanvasState
  currentPermit: Permit
  permitId: number
  organisationId: number
  selectedByPinPermit?: Permit
  setCanvasState: (state: CanvasState) => void
}

export const ProjectPlanPermitsSidebar: React.FC<
  ProjectPlanPermitsSidebarProps
> = ({
  planPermits,
  canvasState,
  currentPermit,
  permitId,
  organisationId,
  selectedByPinPermit,
  setCanvasState,
}) => {
  const { showModal } = useModal()
  const [hoveredState, setHoveredState] = useState<{
    permitId: number
    source: 'list' | 'map'
  }>()
  // Create refs for permit cards
  const permitRefs = useRef<{ [key: number]: HTMLDivElement }>({})
  const containerRef = useRef<HTMLDivElement>(null)

  const filteredPermits =
    planPermits?.getFilteredPermits(
      canvasState.getSelectedShapes().map((shape) => shape.id),
      currentPermit?.id,
      selectedByPinPermit?.id,
    ) ?? []

  // Scroll to the selected pin permit when it changes
  useEffect(() => {
    if (selectedByPinPermit && permitRefs.current[selectedByPinPermit.id]) {
      permitRefs.current[selectedByPinPermit.id].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    } else {
      containerRef.current?.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }, [selectedByPinPermit])

  return (
    <Box
      ref={containerRef}
      sx={{
        minWidth: '19rem',
        maxWidth: '19rem',
        transition: 'width 0.2s ease-out',
        boxShadow: `0px 3px 5px -1px ${alpha('#000', 0.2)}`,
        overflow: 'scroll',
        background: '#f5f5f5',
        position: 'relative',
        overflowX: 'hidden',
        justifySelf: 'center',
      }}
    >
      <Flex
        pt={0.5}
        alignItems='center'
        position='sticky'
        top={0}
        sx={{ background: '#f5f5f5' }}
        zIndex={1}
        flexDirection='column'
      >
        <Text noWrap alignSelf='start' pl={0.7} pb={0.5}>
          {`[${filteredPermits?.length}] ${localizedStrings.permits}`}
        </Text>
      </Flex>

      {filteredPermits?.map((permit) => {
        return (
          <Box
            key={permit?.id}
            sx={{ mr: 0, ml: 1.5 }}
            ref={(el) => (permitRefs.current[permit.id] = el)}
          >
            <PermitCard
              permit={permit}
              onMouseOver={() => {
                setHoveredState({
                  permitId: permit?.id,
                  source: 'list',
                })
              }}
              onClick={() => {
                const newState = canvasState.selectPins(
                  permit.projectLocations?.getPins() ?? [],
                )
                setCanvasState(newState)
              }}
              onMouseLeave={() => setHoveredState(null)}
              hovered={hoveredState?.permitId === permit?.id}
              highlighted={permit?.id === selectedByPinPermit?.id}
              selected={permit?.id === permitId}
              actions={[
                {
                  label: localizedStrings.details,
                  onClick: () =>
                    showModal(
                      <PermitDetailsModal
                        organisationId={organisationId}
                        permitId={permit?.id}
                      />,
                    ),
                },
              ]}
            />
          </Box>
        )
      })}
    </Box>
  )
}
